import React from "react";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { PreviewDrawer } from "@ippon/shared-components";
import PrivateAreaDetailPage from "@/pages/properties/PrivateArea/Detail";
import PaymentDetailPage from "@/pages/Payments/Detail";
import {
  PreviewDrawerNavContext,
  type PaymentDetailNavArgs,
} from "@/components/PropertyDetail/shared/PreviewDrawerNavContext";

interface Props {
  open: boolean;
  onClose: () => void;
  propertyId: string;
  privateAreaId: string | null;
  /** プレビュー時の初期タブ index (未指定は区画の既定タブ)。 */
  initialTab?: number;
  /** テナント管理タブ時の初期サブタブ index。 */
  initialSubTab?: number;
  /** 全画面展開時の遷移先 search (例: ?tab=1&subtab=0)。未指定は素のページ。 */
  expandSearch?: string;
  /** 全画面展開時に渡す location.state。 */
  expandState?: Record<string, unknown>;
}

/** Notion 風プレビュードロワー: 区画詳細ページを埋め込み（展開時は /units/:id へ） */
const PrivateAreaDetailDrawer: React.FC<Props> = ({
  open,
  onClose,
  propertyId,
  privateAreaId,
  initialTab,
  initialSubTab,
  expandSearch,
  expandState,
}) => {
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = React.useState(initialTab ?? 0);
  const [currentSubTab, setCurrentSubTab] = React.useState<number | undefined>(
    initialSubTab,
  );
  // ドロワー内で開く入金詳細 (null = 区画詳細を表示)。2重ドロワーを避けコンテンツ切替する。
  const [paymentNav, setPaymentNav] =
    React.useState<PaymentDetailNavArgs | null>(null);

  // 別の区画を開いた場合は、その区画を開くために渡された初期タブへ戻す。
  React.useEffect(() => {
    if (!open) return;
    setCurrentTab(initialTab ?? 0);
    setCurrentSubTab(initialSubTab);
    setPaymentNav(null); // 別区画/再オープン時は入金詳細ビューを解除
  }, [open, privateAreaId, initialTab, initialSubTab]);

  const openPage = () => {
    // 入金詳細ビュー中は入金詳細の全画面へ遷移
    if (paymentNav) {
      const qs = new URLSearchParams();
      qs.set("property_section_id", paymentNav.propertySectionId);
      if (paymentNav.propertyId) qs.set("property_id", paymentNav.propertyId);
      if (paymentNav.fromSource === "income") qs.set("context", "income");
      navigate(`/payments/${paymentNav.paymentId}?${qs.toString()}`, {
        state: {
          propName: paymentNav.propertyName,
          propertyId: paymentNav.propertyId,
          privateAreaId: paymentNav.propertySectionId,
          privateAreaName: paymentNav.unitName,
          fromSource: paymentNav.fromSource ?? "payments",
          backPath:
            paymentNav.fromSource === "income" && paymentNav.propertyId
              ? `/properties/${paymentNav.propertyId}?tab=5&subtab=1`
              : undefined,
        },
      });
      onClose();
      return;
    }
    if (!privateAreaId) return;
    const searchParams = new URLSearchParams(expandSearch ?? "");
    searchParams.set("tab", String(currentTab));
    if (currentSubTab !== undefined) {
      searchParams.set("subtab", String(currentSubTab));
    } else {
      searchParams.delete("subtab");
    }
    const search = `?${searchParams.toString()}`;
    navigate(
      `/properties/${propertyId}/units/${privateAreaId}${search}`,
      expandState ? { state: expandState } : undefined,
    );
    onClose();
  };
  return (
    <PreviewDrawer open={open} onClose={onClose} onExpand={openPage}>
      <PreviewDrawerNavContext.Provider
        value={{ openPaymentDetail: setPaymentNav }}
      >
        {open && privateAreaId && (
          <>
            {/* 区画詳細は常にマウントしたまま、入金詳細表示中は display:none で隠す。
                これでタブ(例: テナント>入金管理)やスクロール位置を保持し、戻ると元の状態に復帰する。
                height:100% は必須。区画詳細のテナントタブはルートが height:100% で
                グレー背景を flex:1 で下端まで伸ばす設計のため、この中間ラッパが
                高さ auto (block) だと height:100% の解決先が消え、グレーがカード直下で
                途切れる。PreviewDrawer の content(flex:1=確定高さ) をここで素通しする。 */}
            <Box
              sx={{
                display: paymentNav ? "none" : "block",
                height: "100%",
                minHeight: 0,
              }}
            >
              <PrivateAreaDetailPage
                // 別の区画/設備を続けて開いた時に初期タブを正しく取り直すため、
                // id+tab で remount する。
                key={`${privateAreaId}:${initialTab ?? ""}`}
                overridePropertyId={propertyId}
                overridePrivateAreaId={privateAreaId}
                overrideInitialTab={initialTab}
                overrideInitialSubTab={initialSubTab}
                onEmbeddedTabChange={({ tab, subtab }) => {
                  setCurrentTab(tab);
                  setCurrentSubTab(subtab);
                }}
                embedded
              />
            </Box>
            {paymentNav && (
              // 入金詳細ビュー(2重ドロワーを避け同一ドロワー内で表示)。戻るで区画詳細へ。
              <PaymentDetailPage
                key={paymentNav.paymentId}
                overridePaymentId={paymentNav.paymentId}
                overridePropertySectionId={paymentNav.propertySectionId}
                overridePropertyId={paymentNav.propertyId}
                incomeContext={paymentNav.fromSource === "income"}
                embedded
                onBack={() => setPaymentNav(null)}
                onUpdated={paymentNav.onUpdated}
              />
            )}
          </>
        )}
      </PreviewDrawerNavContext.Provider>
    </PreviewDrawer>
  );
};

export default PrivateAreaDetailDrawer;
